/**
 * Configuration Loaders for Different Sources
 */

/**
 * Load configuration from environment-specific files
 */
export const environmentLoader = async (environment) => {
  try {
    const config = await import(`../environments/${environment}.js`);
    return config.default;
  } catch (error) {
    // console.warn(`No environment config found for: ${environment}`);
    return null;
  }
};

/**
 * Load configuration from external JSON file
 */
export const externalJsonLoader = async (environment) => {
  try {
    // Use relative path that works with any deployment subdirectory
    const response = await fetch(`./config/${environment}.json`);
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.warn(`Failed to load external config for ${environment}:`, error);
  }
  
  try {
    // Try to load default config with relative path
    const response = await fetch('./config/default.json');
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.warn('Failed to load default external config:', error);
  }
  
  return null;
};

/**
 * Load configuration from external JSON file with dynamic base path
 */
export const createExternalJsonLoader = (basePath = '') => {
  return async (environment) => {
    // Try multiple path configurations
    const pathsToTry = [
      // Relative path (works for most cases)
      `./config/${environment}.json`,
      // Base path + relative
      basePath ? `${basePath}/config/${environment}.json` : null,
      // Absolute path as fallback
      `/config/${environment}.json`
    ].filter(Boolean);

    for (const configPath of pathsToTry) {
      try {
        console.log(`Trying to load config from: ${configPath}`);
        const response = await fetch(configPath);
        if (response.ok) {
          console.log(`Successfully loaded config from: ${configPath}`);
          return await response.json();
        }
      } catch (error) {
        console.warn(`Failed to load config from ${configPath}:`, error.message);
      }
    }
    
    // Try default config with same path strategy
    const defaultPaths = [
      `./config/default.json`,
      basePath ? `${basePath}/config/default.json` : null,
      `/config/default.json`
    ].filter(Boolean);

    for (const defaultPath of defaultPaths) {
      try {
        console.log(`Trying to load default config from: ${defaultPath}`);
        const response = await fetch(defaultPath);
        if (response.ok) {
          console.log(`Successfully loaded default config from: ${defaultPath}`);
          return await response.json();
        }
      } catch (error) {
        console.warn(`Failed to load default config from ${defaultPath}:`, error.message);
      }
    }
    
    console.warn(`No external config found for environment: ${environment}`);
    return null;
  };
};

/**
 * Load configuration from local storage
 */
export const localStorageLoader = async (environment) => {
  try {
    const configKey = `app-config-${environment}`;
    const storedConfig = localStorage.getItem(configKey);
    if (storedConfig) {
      return JSON.parse(storedConfig);
    }
  } catch (error) {
    console.warn('Failed to load config from localStorage:', error);
  }
  return null;
};

/**
 * Load configuration from remote API
 */
export const remoteApiLoader = async (environment) => {
  try {
    const configUrl = process.env.REACT_APP_CONFIG_URL;
    if (!configUrl) {
      return null;
    }

    const response = await fetch(`${configUrl}/${environment}`);
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.warn('Failed to load config from remote API:', error);
  }
  return null;
};

/**
 * Load configuration from window global object
 */
export const windowGlobalLoader = async (environment) => {
  try {
    if (typeof window !== 'undefined' && window.APP_CONFIG) {
      return window.APP_CONFIG[environment] || window.APP_CONFIG;
    }
  } catch (error) {
    console.warn('Failed to load config from window global:', error);
  }
  return null;
};

/**
 * Save configuration to local storage
 */
export const saveConfigToLocalStorage = (environment, config) => {
  try {
    const configKey = `app-config-${environment}`;
    localStorage.setItem(configKey, JSON.stringify(config));
    return true;
  } catch (error) {
    console.error('Failed to save config to localStorage:', error);
    return false;
  }
};

/**
 * Clear configuration from local storage
 */
export const clearConfigFromLocalStorage = (environment = null) => {
  try {
    if (environment) {
      localStorage.removeItem(`app-config-${environment}`);
    } else {
      // Clear all app configs
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('app-config-')) {
          localStorage.removeItem(key);
        }
      });
    }
    return true;
  } catch (error) {
    console.error('Failed to clear config from localStorage:', error);
    return false;
  }
};
